import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Skeleton,
  Link,
} from '@mui/material';
import {
  Book,
  AttachMoney,
  SportsSoccer,
  TrendingUp,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { dashboardAPI } from '../services/api';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);


const DashboardPage = () => {
  const [metrics, setMetrics] = useState({
    todaysBookings: 0,
    todaysRevenue: 0,
    activeCourts: 0,
    pendingPayments: 0,
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [trendLabels, setTrendLabels] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [sportLabels, setSportLabels] = useState([]);
  const [sportData, setSportData] = useState([]);
  const [trendDays, setTrendDays] = useState(7);
  const [sportDays, setSportDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    fetchChartdata();
  }, [trendDays]);

  useEffect(() => {
    fetchSportData();
  }, [sportDays]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [metricsResponse, bookingsResponse] = await Promise.all([
        dashboardAPI.getMetrics(),
        dashboardAPI.getRecentBookings(),
      ]);
      setMetrics(metricsResponse.data.data);
      setRecentBookings(bookingsResponse.data.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChartdata = async () => {
    try {
      setChartsLoading(true);
      const response = await dashboardAPI.getBookingTrend({ days: trendDays });
      setTrendLabels(response.data.data.labels);
      setTrendData(response.data.data.data);
    } catch (error) {
      console.error('Error fetching booking trend:', error);
    } finally {
      setChartsLoading(false);
    }
  };

  const fetchSportData = async () => {
    try {
      const response = await dashboardAPI.getRevenueBySport({ days: sportDays });
      setSportLabels(response.data.data.labels);
      setSportData(response.data.data.data);
    } catch (error) {
      console.error('Error fetching revenue by sport:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
      case 'paid':
        return 'success';
      case 'requested':
      case 'pending_on_arrival':
        return 'warning';
      case 'cancelled':
      case 'no_show':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ width: '100%', textAlign: 'left' }}>
      

      {/* Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4, width: '100%' }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Book color="primary" sx={{ mr: 1 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Today's Bookings
                  </Typography>
                  <Typography variant="h4">
                    {loading ? '...' : metrics.todaysBookings}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AttachMoney color="success" sx={{ mr: 1 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Today's Revenue
                  </Typography>
                  <Typography variant="h4">
                    {loading ? '...' : `₹${metrics.todaysRevenue}`}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SportsSoccer color="secondary" sx={{ mr: 1 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active Courts
                  </Typography>
                  <Typography variant="h4">
                    {loading ? '...' : metrics.activeCourts}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUp color="warning" sx={{ mr: 1 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Pending Payments
                  </Typography>
                  <Typography variant="h4">
                    {loading ? '...' : metrics.pendingPayments}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4, width: '100%' }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6" gutterBottom>
                  Booking Trend
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Period</InputLabel>
                  <Select
                    value={trendDays}
                    label="Period"
                    onChange={(e) => setTrendDays(e.target.value)}
                  >
                    <MenuItem value={7}>Last 7 days</MenuItem>
                    <MenuItem value={14}>Last 14 days</MenuItem>
                    <MenuItem value={30}>Last 30 days</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <Box sx={{ height: 300 }}>
                {chartsLoading ? (
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Typography color="textSecondary">Loading chart...</Typography>
                  </Box>
                ) : trendData.length === 0 ? (
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Typography color="textSecondary">No booking data available</Typography>
                  </Box>
                ) : (
                  <Line
                    data={{
                      labels: trendLabels,
                      datasets: [
                        {
                          label: 'Bookings',
                          data: trendData,
                          borderColor: 'rgb(75, 192, 192)',
                          backgroundColor: 'rgba(75, 192, 192, 0.2)',
                          tension: 0.1,
                          fill: true,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                    }}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6" gutterBottom>
                  Revenue by Sport
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Period</InputLabel>
                  <Select
                    value={sportDays}
                    label="Period"
                    onChange={(e) => setSportDays(e.target.value)}
                  >
                    <MenuItem value={7}>Last 7 days</MenuItem>
                    <MenuItem value={30}>Last 30 days</MenuItem>
                    <MenuItem value={90}>Last 90 days</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <Box sx={{ height: 300 }}>
                {sportData.length === 0 ? (
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Typography color="textSecondary">No revenue data available</Typography>
                  </Box>
                ) : (
                  <Bar
                    data={{
                      labels: sportLabels,
                      datasets: [
                        {
                          label: 'Revenue',
                          data: sportData,
                          backgroundColor: [
                            'rgba(255, 99, 132, 0.6)',
                            'rgba(54, 162, 235, 0.6)',
                            'rgba(255, 205, 86, 0.6)',
                            'rgba(75, 192, 192, 0.6)',
                            'rgba(153, 102, 255, 0.6)',
                            'rgba(255, 159, 64, 0.6)',
                            'rgba(199, 199, 199, 0.6)',
                            'rgba(83, 102, 255, 0.6)',
                          ],
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                    }}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

{/* Recent Bookings Table */}
      <Card sx={{ width: '100%' }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6" gutterBottom>
              Recent Bookings
            </Typography>
            <Link href="/bookings" variant="body2" sx={{ textDecoration: 'none' }}>
              View all →
            </Link>
          </Stack>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Booking ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Sport</TableCell>
                  <TableCell>Court</TableCell>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={7}>
                        <Skeleton height={28} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : recentBookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="textSecondary" py={2}>
                        No recent bookings
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  recentBookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell>{booking.id}</TableCell>
                      <TableCell>{booking.user_name || booking.user}</TableCell>
                      <TableCell>{booking.sport_name || booking.sport}</TableCell>
                      <TableCell>{booking.court_label || booking.court}</TableCell>
                      <TableCell>
                        {booking.date}
                        <br />
                        {booking.start_time}-{booking.end_time}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={booking.status}
                          color={getStatusColor(booking.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">₹{booking.amount}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DashboardPage;